import { useEffect, useState } from 'react';
import SignaturePad from '../../components/ui/SignaturePad';
import { apiUrl, request, storageUrl } from '../../services/api';

export default function PublicSigningPage() {
  const token = window.location.pathname.match(/\/contracts\/sign\/([^/]+)/)?.[1];
  const [data, setData] = useState(null);
  const [form, setForm] = useState({});
  const [signature, setSignature] = useState('');
  const [file, setFile] = useState(null);
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    request(`/api/contracts/public/${token}`)
      .then((response) => {
        setData(response.data);
        setForm({
          recipient_full_name: response.data.contract.recipient_full_name || '',
          recipient_email: response.data.contract.recipient_email || '',
          recipient_phone: response.data.contract.recipient_phone || '',
          recipient_address: response.data.contract.recipient_address || '',
          recipient_national_id: response.data.contract.recipient_national_id || '',
          emergency_contact: response.data.contract.emergency_contact || '',
        });
        setLoading(false);
      })
      .catch((err) => {
        setMessage(err.message);
        setLoading(false);
      });
  }, [token]);

  const handleInlineInput = (e) => {
    if (e.target.tagName === 'INPUT' && e.target.dataset.field) {
      const field = e.target.dataset.field;
      const value = e.target.value;
      setForm((prev) => ({
        ...prev,
        [field]: value,
      }));
    }
  };

  const submit = async (event) => {
    event.preventDefault();
    const body = new FormData();
    Object.entries(form).forEach(([key, value]) => body.append(key, value));
    if (file) body.append('signature_image', file);
    if (signature) body.append('signature_data_url', signature);

    try {
      const response = await request(`/api/contracts/public/${token}/sign`, {
        method: 'POST',
        body,
        form: true,
      });
      setMessage('Signed successfully. Preparing your PDF download...');
      setTimeout(() => {
        window.location.href = apiUrl(response.download_url);
      }, 1000);
    } catch (err) {
      setMessage(err.message || 'Error signing contract.');
    }
  };

  if (message && !data) {
    return (
      <main className="public-shell">
        <div className="alert danger">{message}</div>
      </main>
    );
  }

  if (loading) {
    return (
      <main className="public-shell">
        <div className="empty">Loading secure signing interface...</div>
      </main>
    );
  }

  const { contract, company } = data;

  // Let's create an interactive version of the text template with inline fields!
  const getInteractiveContent = () => {
    let content = contract.generated_content || '';
    if (!content) return '';

    const placeholders = {
      recipient_full_name: ['{{recipient_full_name}}', '{{recipient_name}}', '{{full_name}}', '{{name}}'],
      recipient_email: ['{{recipient_email}}', '{{email}}'],
      recipient_phone: ['{{recipient_phone}}', '{{phone}}'],
      recipient_address: ['{{recipient_address}}', '{{address}}'],
      recipient_national_id: ['{{recipient_national_id}}', '{{national_id}}', '{{nida}}'],
      emergency_contact: ['{{emergency_contact}}'],
    };

    Object.entries(placeholders).forEach(([field, patterns]) => {
      patterns.forEach((pattern) => {
        const val = form[field] || '';
        const inputHtml = `<input type="text" class="inline-blank-input" data-field="${field}" value="${val.replace(/"/g, '&quot;')}" placeholder="[Fill ${field.replaceAll('_', ' ')}]" />`;
        content = content.replaceAll(pattern, inputHtml);
      });
    });

    return content;
  };

  const hasGeneratedContentPlaceholders = 
    contract.generated_content && 
    (contract.generated_content.includes('{{') || contract.generated_content.includes('recipient_') || contract.generated_content.includes('name'));

  return (
    <main className="public-signing-view">
      <header className="public-top-bar">
        <div className="logo-title-group">
          {company?.company_logo && <img src={storageUrl(company.company_logo)} className="company-logo-sig" alt="" />}
          <div>
            <p className="eyebrow">{company?.company_name || 'Secure Contract Portal'}</p>
            <h1>{contract.title}</h1>
          </div>
        </div>
        <a className="ghost download-btn" href={apiUrl(`/api/contracts/public/${token}/download`)}>
          📥 Download Original PDF
        </a>
      </header>

      <div className="signing-workspace-layout">
        {/* Document Viewer (Left Column) */}
        <section className="workspace-document-panel">
          <div className="workspace-header">
            <h3>📝 Contract Document</h3>
            <span className="secure-badge">🔒 End-to-End Secure Signing</span>
          </div>

          <div className="viewer-scroll-area">
            {contract.pdf_path && (
              <div className="pdf-iframe-holder">
                <iframe title={contract.title} src={apiUrl(`/api/contracts/public/${token}/preview`)} />
              </div>
            )}

            {contract.generated_content && (
              <div 
                className="interactive-document-container" 
                onInput={handleInlineInput}
                dangerouslySetInnerHTML={{ __html: getInteractiveContent() }}
              />
            )}
          </div>
        </section>

        {/* Input Details & Signature Box (Right Column) */}
        <section className="workspace-sidebar-panel">
          <div className="workspace-header">
            <h3>✍️ Complete and Sign</h3>
          </div>

          <form onSubmit={submit} className="sidebar-form-stack">
            <div className="inputs-scroller">
              <p className="sidebar-instructions">
                {hasGeneratedContentPlaceholders 
                  ? "You can fill the details directly in the document blanks on the left, or complete the fields below. They will automatically sync."
                  : "Please verify and complete your legal details below. Your information will be securely compiled into the final contract PDF."
                }
              </p>

              <div className="form-group-item">
                <label>Full legal name</label>
                <input 
                  type="text" 
                  value={form.recipient_full_name || ''} 
                  onChange={(e) => setForm({ ...form, recipient_full_name: e.target.value })} 
                  required 
                  placeholder="John Doe" 
                />
              </div>

              <div className="form-group-item">
                <label>Email address</label>
                <input 
                  type="email" 
                  value={form.recipient_email || ''} 
                  onChange={(e) => setForm({ ...form, recipient_email: e.target.value })} 
                  required 
                  placeholder="john@example.com" 
                />
              </div>

              <div className="form-group-item">
                <label>Phone number</label>
                <input 
                  type="tel" 
                  value={form.recipient_phone || ''} 
                  onChange={(e) => setForm({ ...form, recipient_phone: e.target.value })} 
                  required 
                  placeholder="+255..." 
                />
              </div>

              <div className="form-group-item">
                <label>Physical Address</label>
                <input 
                  type="text" 
                  value={form.recipient_address || ''} 
                  onChange={(e) => setForm({ ...form, recipient_address: e.target.value })} 
                  required 
                  placeholder="City, Country" 
                />
              </div>

              <div className="form-group-item">
                <label>National ID / NIDA</label>
                <input 
                  type="text" 
                  value={form.recipient_national_id || ''} 
                  onChange={(e) => setForm({ ...form, recipient_national_id: e.target.value })} 
                  required 
                  placeholder="199XXXXXXXXXXXXXXX" 
                />
              </div>

              <div className="form-group-item">
                <label>Emergency Contact</label>
                <input 
                  type="text" 
                  value={form.emergency_contact || ''} 
                  onChange={(e) => setForm({ ...form, emergency_contact: e.target.value })} 
                  required 
                  placeholder="Spouse / Parent (Name & Phone)" 
                />
              </div>

              <div className="form-group-item">
                <label>Upload signature image (Optional)</label>
                <input type="file" accept="image/*" onChange={(e) => setFile(e.target.files[0])} />
              </div>

              <div className="form-group-item">
                <label>Draw your signature</label>
                <SignaturePad onChange={setSignature} />
              </div>
            </div>

            <div className="submit-sticky-footer">
              <button type="submit" className="primary full-btn-width">
                🖋️ Sign and Download Signed PDF
              </button>
              {message && <div className="alert success">{message}</div>}
            </div>
          </form>
        </section>
      </div>
    </main>
  );
}
